import React from 'react';
import { Modal, Typography, Divider, Row, Col, Space, Tag } from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

/**
 * Modal hiển thị chi tiết refund cho order item
 * Tương tự Transaction Details từ Amazon Seller Central
 */
const RefundDetailsModal = ({ visible, onCancel, refundDetails, orderItem }) => {
    if (!refundDetails || refundDetails.length === 0) {
        return null;
    }

    // Calculate total refund amount
    const totalRefund = refundDetails.reduce((sum, refund) => sum + (refund.netImpact || 0), 0);

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return '$0.00';
        const num = parseFloat(amount);
        const sign = num < 0 ? '-' : '';
        return `${sign}$${Math.abs(num).toFixed(2)}`;
    };

    const renderCharges = (charges) => {
        if (!charges) return null;

        return (
            <div style={{ marginBottom: 24 }}>
                <Title level={5} style={{ marginBottom: 12 }}>Product charges</Title>
                {charges.principal && (
                    <Row justify="space-between" style={{ marginBottom: 4 }}>
                        <Col>
                            <Text>{orderItem?.productName || 'Product'}</Text>
                            <Text type="secondary" style={{ marginLeft: 8 }}>Qty: 1</Text>
                        </Col>
                        <Col>
                            <Text strong style={{ color: '#d32f2f' }}>{formatCurrency(charges.principal)}</Text>
                        </Col>
                    </Row>
                )}
                
                {/* Other section */}
                {(charges.shipping || charges.tax || charges.shippingTax || charges.giftWrap || charges.giftWrapTax) && (
                    <>
                        <Title level={5} style={{ marginTop: 16, marginBottom: 12 }}>Other</Title>
                        {charges.shipping && (
                            <Row justify="space-between" style={{ marginBottom: 4 }}>
                                <Col><Text>Shipping:</Text></Col>
                                <Col><Text strong style={{ color: '#d32f2f' }}>{formatCurrency(charges.shipping)}</Text></Col>
                            </Row>
                        )}
                        {charges.tax && (
                            <Row justify="space-between" style={{ marginBottom: 4 }}>
                                <Col><Text>Tax:</Text></Col>
                                <Col><Text strong style={{ color: '#d32f2f' }}>{formatCurrency(charges.tax)}</Text></Col>
                            </Row>
                        )}
                        {charges.shippingTax && (
                            <Row justify="space-between" style={{ marginBottom: 4 }}>
                                <Col><Text>Shipping Tax:</Text></Col>
                                <Col><Text strong style={{ color: '#d32f2f' }}>{formatCurrency(charges.shippingTax)}</Text></Col>
                            </Row>
                        )}
                        {charges.giftWrap && (
                            <Row justify="space-between" style={{ marginBottom: 4 }}>
                                <Col><Text>Gift Wrap:</Text></Col>
                                <Col><Text strong style={{ color: '#d32f2f' }}>{formatCurrency(charges.giftWrap)}</Text></Col>
                            </Row>
                        )}
                        {charges.giftWrapTax && (
                            <Row justify="space-between" style={{ marginBottom: 4 }}>
                                <Col><Text>Gift Wrap Tax:</Text></Col>
                                <Col><Text strong style={{ color: '#d32f2f' }}>{formatCurrency(charges.giftWrapTax)}</Text></Col>
                            </Row>
                        )}
                    </>
                )}

                {/* Refund to Buyer */}
                <Divider style={{ margin: '12px 0' }} />
                <Row justify="space-between" style={{ marginBottom: 12 }}>
                    <Col><Text strong>Refund to Buyer</Text></Col>
                    <Col><Text strong style={{ color: '#d32f2f' }}>{formatCurrency(charges.total)}</Text></Col>
                </Row>
            </div>
        );
    };

    const renderFees = (fees) => {
        if (!fees || Object.keys(fees).length <= 1) return null; // Only 'total' key

        return (
            <div style={{ marginBottom: 24 }}>
                <Title level={5} style={{ marginBottom: 12 }}>Amazon fees</Title>
                {Object.entries(fees).map(([feeType, amount]) => {
                    if (feeType === 'total') return null;
                    // Backend đã filter amount === 0, không cần check nữa
                    
                    // Format fee type name
                    let displayName = feeType;
                    if (feeType === 'Commission') displayName = 'Referral Fee on Item Price:';
                    else if (feeType === 'RefundCommission') displayName = 'Refund Administration Fee:';
                    else if (feeType === 'ShippingHB') displayName = 'Shipping commission:';
                    
                    const isNegative = amount < 0;
                    
                    return (
                        <Row key={feeType} justify="space-between" style={{ marginBottom: 8 }}>
                            <Col span={12}>
                                <Text>{displayName}</Text>
                            </Col>
                            <Col span={6} style={{ textAlign: 'right' }}>
                                <Space direction="vertical" size={0}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Base amount</Text>
                                    {feeType === 'RefundCommission' && <Text type="secondary" style={{ fontSize: 12 }}>Tax</Text>}
                                </Space>
                            </Col>
                            <Col span={6} style={{ textAlign: 'right' }}>
                                <Space direction="vertical" size={0}>
                                    <Text strong style={{ color: isNegative ? '#d32f2f' : '#4caf50' }}>
                                        {formatCurrency(amount)}
                                    </Text>
                                    {feeType === 'RefundCommission' && (
                                        <Text strong style={{ color: isNegative ? '#d32f2f' : '#4caf50' }}>
                                            {formatCurrency(amount * 0.08)}
                                        </Text>
                                    )}
                                </Space>
                            </Col>
                        </Row>
                    );
                })}
            </div>
        );
    };

    const renderTaxWithheld = (taxWithheld) => {
        if (!taxWithheld || !taxWithheld.total || taxWithheld.total === 0) return null;

        return (
            <div style={{ marginBottom: 24 }}>
                <Title level={5} style={{ marginBottom: 12 }}>Tax Withheld</Title>
                {taxWithheld.marketplaceFacilitatorTax && (
                    <Row justify="space-between" style={{ marginBottom: 4 }}>
                        <Col><Text>Marketplace Facilitator Tax:</Text></Col>
                        <Col><Text strong style={{ color: '#4caf50' }}>{formatCurrency(taxWithheld.marketplaceFacilitatorTax)}</Text></Col>
                    </Row>
                )}
            </div>
        );
    };

    const renderPromotions = (promotions) => {
        if (!promotions || !promotions.adjustments || promotions.adjustments.length === 0) return null;

        return (
            <div style={{ marginBottom: 24 }}>
                <Title level={5} style={{ marginBottom: 12 }}>Promotions</Title>
                {promotions.adjustments.map((promo, idx) => (
                    <Row key={idx} justify="space-between" style={{ marginBottom: 4 }}>
                        <Col>
                            <Text>{promo.type || 'Promotion'}</Text>
                            {promo.id && <Text type="secondary" style={{ marginLeft: 8, fontSize: 11 }}>({promo.id})</Text>}
                        </Col>
                        <Col><Text strong style={{ color: promo.amount < 0 ? '#d32f2f' : '#4caf50' }}>{formatCurrency(promo.amount)}</Text></Col>
                    </Row>
                ))}
            </div>
        );
    };

    return (
        <Modal
            title={
                <Space>
                    <DollarOutlined style={{ color: '#1890ff' }} />
                    <span>Refund Details for Order {orderItem?.order?.amazonOrderId}</span>
                </Space>
            }
            open={visible}
            onCancel={onCancel}
            footer={null}
            width={700}
        >
            {refundDetails.map((refund, index) => (
                <div key={index} style={{ 
                    backgroundColor: '#f5f5f5', 
                    padding: 20, 
                    marginBottom: index < refundDetails.length - 1 ? 16 : 0,
                    borderRadius: 8 
                }}>
                    {/* Header */}
                    <div style={{ marginBottom: 16 }}>
                        <Title level={4} style={{ marginBottom: 4 }}>
                            Refund for Order {orderItem?.order?.amazonOrderId}
                        </Title>
                        <Text type="secondary">
                            <strong>Transaction date:</strong> {dayjs(refund.refundDate).format('M/D/YYYY')}
                        </Text>
                    </div>

                    {/* Charges - Product & Other */}
                    {renderCharges(refund.charges)}

                    {/* Amazon Fees */}
                    {renderFees(refund.fees)}

                    {/* Tax Withheld */}
                    {renderTaxWithheld(refund.taxWithheld)}

                    {/* Promotions */}
                    {renderPromotions(refund.promotions)}

                    {/* Final Total */}
                    <Divider style={{ margin: '16px 0', borderColor: '#333' }} />
                    <Row justify="space-between">
                        <Col>
                            <Text strong style={{ fontSize: 16 }}>Change to your seller account balance</Text>
                        </Col>
                        <Col>
                            <Text strong style={{ fontSize: 16, color: refund.netImpact < 0 ? '#d32f2f' : '#4caf50' }}>
                                {formatCurrency(refund.netImpact)}
                            </Text>
                        </Col>
                    </Row>
                </div>
            ))}

            {/* Summary if multiple refunds */}
            {refundDetails.length > 1 && (
                <div style={{ 
                    marginTop: 16, 
                    padding: 16, 
                    backgroundColor: '#e3f2fd', 
                    borderRadius: 8,
                    borderLeft: '4px solid #1890ff'
                }}>
                    <Row justify="space-between">
                        <Col>
                            <Text strong style={{ fontSize: 16 }}>Total Refunds ({refundDetails.length} transactions)</Text>
                        </Col>
                        <Col>
                            <Text strong style={{ fontSize: 18, color: totalRefund < 0 ? '#d32f2f' : '#4caf50' }}>
                                {formatCurrency(totalRefund)}
                            </Text>
                        </Col>
                    </Row>
                </div>
            )}
        </Modal>
    );
};

export default RefundDetailsModal;
